import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Ticket, Plus, MessageSquare, AlertCircle, Clock, CheckCircle, XCircle,
  Send, Filter, Search, Building2, User, Calendar, Upload, FileText, X
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketsPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'technical',
    attachments: []
  });
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, [statusFilter, priorityFilter]);

  const fetchData = async () => {
    try {
      let url = `${API_URL}/api/tickets`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const ticketsRes = await axios.get(url, { headers: getAuthHeader() });
      setTickets(ticketsRes.data);
      
      if (isSuperAdmin) {
        const statsRes = await axios.get(`${API_URL}/api/tickets/stats/overview`, { headers: getAuthHeader() });
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      await axios.post(`${API_URL}/api/tickets`, newTicket, { headers: getAuthHeader() });
      toast.success('Ticket created successfully');
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', description: '', category: 'technical', attachments: [] });
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create ticket';
      toast.error(errorMessage);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check max files
    if (newTicket.attachments.length + files.length > 4) {
      toast.error('Maximum 4 files allowed');
      return;
    }
    
    setUploadingFiles(true);
    const uploadedFiles = [];
    
    for (const file of files) {
      // Check file size (150KB)
      if (file.size > 150 * 1024) {
        toast.error(`${file.name} is too large. Maximum 150KB allowed`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API_URL}/api/tickets/upload-attachment`,
          formData,
          { 
            headers: { 
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        uploadedFiles.push({
          name: response.data.filename,
          url: response.data.url,
          size: String(response.data.size),
          id: response.data.id
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadedFiles.length > 0) {
      setNewTicket({
        ...newTicket,
        attachments: [...newTicket.attachments, ...uploadedFiles]
      });
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    }
    
    setUploadingFiles(false);
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (index) => {
    const newAttachments = [...newTicket.attachments];
    newAttachments.splice(index, 1);
    setNewTicket({ ...newTicket, attachments: newAttachments });
  };

  // Download attachment with authentication
  const downloadAttachment = async (att) => {
    try {
      const response = await axios.get(
        `${API_URL}${att.url}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.name || att.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      console.log('[Tickets] Fetching ticket:', ticketId);
      const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`, { headers: getAuthHeader() });
      console.log('[Tickets] Ticket data received:', response.data);
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('[Tickets] Failed to load ticket:', error.response?.data || error.message);
      toast.error('Failed to load ticket details');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    try {
      await axios.post(
        `${API_URL}/api/tickets/${selectedTicket.id}/reply`,
        { message: replyText, is_internal: isInternalNote },
        { headers: getAuthHeader() }
      );
      toast.success('Reply sent');
      setReplyText('');
      handleViewTicket(selectedTicket.id);
      fetchData();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API_URL}/api/tickets/${ticketId}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Status updated');
      handleViewTicket(ticketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdatePriority = async (ticketId, priority) => {
    try {
      await axios.patch(`${API_URL}/api/tickets/${ticketId}/priority?priority=${priority}`, {}, { headers: getAuthHeader() });
      toast.success('Priority updated');
      handleViewTicket(ticketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { variant: 'destructive', icon: AlertCircle },
      in_progress: { variant: 'default', icon: Clock },
      waiting_for_customer: { variant: 'secondary', icon: Clock },
      resolved: { variant: 'outline', icon: CheckCircle },
      closed: { variant: 'outline', icon: XCircle }
    };
    const config = variants[status] || variants.open;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status?.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700',
      medium: 'bg-blue-100 text-blue-700',
      high: 'bg-amber-100 text-amber-700',
      critical: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[priority]}>{priority?.toUpperCase()}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      technical: '🔧',
      billing: '💳',
      feature_request: '💡',
      bug_report: '🐛',
      account: '👤',
      other: '📋'
    };
    return icons[category] || '📋';
  };

  const filteredTickets = tickets.filter(t => 
    t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.school?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Support Tickets">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support Tickets">
      <div className="space-y-6">
        {/* Important Notice - Tenant Only */}
        {!isSuperAdmin && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-5" data-testid="ticket-page-notice">
            <p className="font-bold text-amber-900 text-base mb-3">Important Notice - Ncriptech SMS Software Services</p>
            <p className="text-sm text-amber-800 mb-2">
              Any issues, breakdowns, or technical problems related to the <strong>existing functions and features</strong> provided within the SMS will be resolved by our team promptly and <strong>free of charge</strong>.
            </p>
            <p className="text-sm text-amber-800 mb-2">
              However, requests for <strong>addition of new features, modification or removal of existing modules, custom add-ons, or any institution-specific customization</strong> will be treated as new customization work, and a <strong>separate quotation</strong> will be provided accordingly.
            </p>
            <p className="text-sm text-amber-800 mb-2">
              The requested customisation/development work will commence <strong>only after approval of the quotation and receipt of the applicable payment</strong>.
            </p>
            <p className="text-sm text-amber-800 font-medium">
              We sincerely appreciate your understanding and cooperation in this matter.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Support Tickets</h1>
            <p className="text-muted-foreground">
              {isSuperAdmin ? 'Manage support requests from schools' : 'Get help from our support team'}
            </p>
          </div>
          {!isSuperAdmin && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-ticket-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Ticket
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Support Ticket</DialogTitle>
                </DialogHeader>

                {/* Important Notice */}
                <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 text-sm text-amber-900 space-y-2" data-testid="ticket-notice">
                  <p className="font-bold text-base">Important Notice - Ncriptech Team</p>
                  <p>Any issues or technical problems related to <strong>existing functions</strong> will be resolved promptly and <strong>free of charge</strong>.</p>
                  <p>However, requests for <strong>new features, modifications, custom add-ons, or institution-specific customization</strong> will require a <strong>separate quotation</strong>. Such work will commence only after approval and receipt of payment.</p>
                  <p className="font-medium">Please be advised that new customisation work cannot be undertaken without an approved quotation and corresponding payment.</p>
                </div>

                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input value={newTicket.subject} onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})} placeholder="Brief description of your issue" />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={newTicket.category} onValueChange={(v) => setNewTicket({...newTicket, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technical">Technical Issue</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="feature_request">Feature Request</SelectItem>
                        <SelectItem value="bug_report">Bug Report</SelectItem>
                        <SelectItem value="account">Account</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={newTicket.description} onChange={(e) => setNewTicket({...newTicket, description: e.target.value})} placeholder="Please describe your issue in detail..." rows={5} />
                  </div>
                  
                  {/* File Upload Section */}
                  <div className="space-y-2">
                    <Label>Attachments (Max 4 files, 150KB each)</Label>
                    <div className="border-2 border-dashed rounded-lg p-4">
                      <input
                        type="file"
                        id="ticket-files"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploadingFiles || newTicket.attachments.length >= 4}
                        data-testid="ticket-file-input"
                      />
                      <label 
                        htmlFor="ticket-files" 
                        className={`flex flex-col items-center cursor-pointer ${newTicket.attachments.length >= 4 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">
                          {uploadingFiles ? 'Uploading...' : 'Click to upload files'}
                        </span>
                        <span className="text-xs text-muted-foreground mt-1">
                          PDF, JPG, PNG (Max 150KB)
                        </span>
                      </label>
                    </div>
                    
                    {/* Uploaded Files List */}
                    {newTicket.attachments.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {newTicket.attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                              <span className="text-xs text-muted-foreground">({Math.round(parseInt(file.size) / 1024)}KB)</span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeAttachment(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreateTicket} disabled={!newTicket.subject || !newTicket.description || uploadingFiles}>Submit Ticket</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats (Super Admin only) */}
        {isSuperAdmin && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">Total Tickets</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{stats.open}</div>
                <p className="text-xs text-muted-foreground">Open</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">{stats.in_progress}</div>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">{stats.waiting_for_customer}</div>
                <p className="text-xs text-muted-foreground">Waiting</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
                <p className="text-xs text-muted-foreground">Resolved</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{stats.critical_pending}</div>
                <p className="text-xs text-muted-foreground">Critical Pending</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search tickets..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="waiting_for_customer">Waiting</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Tickets ({filteredTickets.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="p-4 flex flex-col gap-3">
                  {filteredTickets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Ticket className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>No tickets found</p>
                    </div>
                  ) : (
                    filteredTickets.map(ticket => (
                      <div
                        key={ticket.id}
                        data-testid={`ticket-item-${ticket.id}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors flex-shrink-0 ${selectedTicket?.id === ticket.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                        onClick={() => handleViewTicket(ticket.id)}
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg flex-shrink-0">{getCategoryIcon(ticket.category)}</span>
                            <span className="text-xs text-muted-foreground">{ticket.ticket_number}</span>
                            <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                              {getPriorityBadge(ticket.priority)}
                              {getStatusBadge(ticket.status)}
                            </div>
                          </div>
                          <p className="font-medium line-clamp-2">{ticket.subject}</p>
                          {isSuperAdmin && ticket.school && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Building2 className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{ticket.school.name}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          {ticket.reply_count > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {ticket.reply_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Ticket Detail */}
          <Card className="lg:col-span-2">
            {selectedTicket ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{getCategoryIcon(selectedTicket.category)}</span>
                        <span className="text-sm text-muted-foreground">{selectedTicket.ticket_number}</span>
                      </div>
                      <CardTitle>{selectedTicket.subject}</CardTitle>
                      {isSuperAdmin && selectedTicket.school && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Building2 className="h-4 w-4" />
                          {selectedTicket.school.name} ({selectedTicket.school.school_code})
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {getStatusBadge(selectedTicket.status)}
                      {getPriorityBadge(selectedTicket.priority)}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {isSuperAdmin && (
                    <div className="flex gap-2 mt-4">
                      <Select value={selectedTicket.status} onValueChange={(v) => handleUpdateStatus(selectedTicket.id, v)}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="waiting_for_customer">Waiting</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={selectedTicket.priority} onValueChange={(v) => handleUpdatePriority(selectedTicket.id, v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px] p-4">
                    {/* Original Message */}
                    <div className="mb-4 p-4 bg-muted rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{selectedTicket.creator?.name?.[0] || 'U'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{selectedTicket.creator?.name}</p>
                          <p className="text-xs text-muted-foreground">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
                      
                      {/* Attachments */}
                      {selectedTicket.attachments?.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium mb-2">Attachments ({selectedTicket.attachments.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedTicket.attachments.map((att, idx) => (
                              <button
                                key={idx}
                                onClick={() => downloadAttachment(att)}
                                className="flex items-center gap-2 bg-muted px-3 py-2 rounded text-sm hover:bg-muted/80 cursor-pointer"
                              >
                                <FileText className="h-4 w-4" />
                                <span className="truncate max-w-[150px]">{att.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Replies */}
                    {selectedTicket.replies?.map((reply, i) => (
                      <div key={i} className={`mb-4 p-4 rounded-lg ${reply.is_internal ? 'bg-amber-50 border-l-4 border-amber-400' : reply.user?.role === 'super_admin' ? 'bg-blue-50' : 'bg-muted'}`}>
                        {reply.is_internal && (
                          <Badge variant="outline" className="mb-2 text-xs">Internal Note</Badge>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{reply.user?.name?.[0] || 'U'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">
                              {reply.user?.name}
                              {reply.user?.role === 'super_admin' && <Badge className="ml-2 text-xs">Support</Badge>}
                            </p>
                            <p className="text-xs text-muted-foreground">{new Date(reply.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                  </ScrollArea>

                  {/* Reply Input */}
                  <div className="p-4 border-t">
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 mb-2">
                        <input type="checkbox" id="internal" checked={isInternalNote} onChange={(e) => setIsInternalNote(e.target.checked)} className="rounded" />
                        <Label htmlFor="internal" className="text-sm">Internal note (not visible to school)</Label>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write your reply..." className="min-h-[80px]" />
                      <Button onClick={handleSendReply} disabled={!replyText.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex flex-col items-center justify-center h-[600px]">
                <Ticket className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">Select a ticket to view details</p>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TicketsPage;
